import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import PageContainer from '../../../components/layout/PageContainer';
import {
  FBoxContainer,
  FHButton,
  FHPageLink,
  FHSelect,
  FProgressBar,
} from '../../../components/base';
import { FQuillEditor } from '../../../components/baseV2';
import { Themes } from '../../../constants';
import { useFieldTemplates } from './hooks';
import {
  getFieldOptions,
  getIsTemplateReady,
  getResourceOptions,
  getSelectedIdentifier,
} from './utils';

import type { SelectValueType } from '../../../utils/types';

const FieldTemplatesPage = () => {
  const {
    fields,
    fieldsLoading,
    fieldTemplate,
    fieldTemplateLoading,
    updateFieldTemplateLoading,
    loadFields,
    loadTemplate,
    saveTemplate,
  } = useFieldTemplates();

  const [selectedResource, setSelectedResource] = useState('');
  const [selectedField, setSelectedField] = useState('');
  const [editorContent, setEditorContent] = useState('');

  const selectedIdentifier = getSelectedIdentifier(fields, selectedResource, selectedField);

  const isTemplateReady = getIsTemplateReady(selectedIdentifier, fieldTemplateLoading, fieldTemplate);

  useEffect(() => {
    loadFields();
  }, [loadFields]);

  useEffect(() => {
    if (!selectedIdentifier) {
      return;
    }
    setEditorContent('');
    loadTemplate(selectedIdentifier);
  }, [selectedIdentifier, loadTemplate]);

  useEffect(() => {
    if (fieldTemplate && fieldTemplate.identifier === selectedIdentifier) {
      setEditorContent(fieldTemplate.template ?? '');
    }
  }, [fieldTemplate, selectedIdentifier]);

  const handleResourceChange = (value: SelectValueType) => {
    setSelectedResource(String(value));
    setSelectedField('');
  };

  const handleCancelTemplate = () => {
    if (fieldTemplate && fieldTemplate.identifier === selectedIdentifier) {
      setEditorContent(fieldTemplate.template ?? '');
    }
  };

  const handleSaveTemplate = () => {
    if (selectedIdentifier) {
      saveTemplate(selectedIdentifier, editorContent);
    }
  };

  return (
    <PageContainer>
      {(fieldsLoading || fieldTemplateLoading) && <FProgressBar />}
      <Box sx={{ mt: 3 }}>
        <FHPageLink to="/admin" label="Admin Home" />
        <FBoxContainer sx={{ borderRadius: 2, boxShadow: 'none' }}>
          <Box ml={1} sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography textAlign="left" variant="h4">
              Field templates
            </Typography>
          </Box>
          <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', mt: 1, mb: 2 }} />
          <Box sx={{ display: 'flex', mt: 2, alignItems: 'center' }}>
            <Typography textAlign="left" variant="h6" sx={{ width: '230px' }}>
              Resource:
            </Typography>
            <FHSelect
              data-testid="resourceDropdown"
              inputHeight={40}
              displayEmpty
              sx={{ width: 320, boxShadow: 'none' }}
              popoverWidth={320}
              selectFirstOption={false}
              options={getResourceOptions(fields)}
              value={selectedResource}
              optionDataTestIdPrefix="resource_option"
              renderValue={selectedResource ? undefined : () => <Typography>Select</Typography>}
              onUpdate={handleResourceChange}
            />
          </Box>
          <Box sx={{ display: 'flex', mt: 2, alignItems: 'center' }}>
            <Typography textAlign="left" variant="h6" sx={{ width: '230px' }}>
              Field:
            </Typography>
            <FHSelect
              data-testid="fieldDropdown"
              inputHeight={40}
              displayEmpty
              sx={{ width: 320, boxShadow: 'none' }}
              popoverWidth={320}
              selectFirstOption={false}
              disabled={!selectedResource}
              options={getFieldOptions(fields, selectedResource)}
              value={selectedField}
              optionDataTestIdPrefix="field_option"
              renderValue={selectedField ? undefined : () => <Typography>Select</Typography>}
              onUpdate={(value: SelectValueType) => setSelectedField(String(value))}
            />
          </Box>
          <Box sx={{ mt: 3 }}>
            <FQuillEditor
              disabled={!isTemplateReady}
              label="Add template content"
              value={editorContent}
              onChange={setEditorContent}
              placeholder="Add template content"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <FHButton
                data-testid="cancelTemplateButton"
                secondary
                disabled={updateFieldTemplateLoading || !isTemplateReady}
                onClick={handleCancelTemplate}
              >
                Cancel
              </FHButton>
              <FHButton
                disabled={updateFieldTemplateLoading || !isTemplateReady}
                data-testid="saveTemplateButton"
                colorTheme={Themes.Primary}
                loading={updateFieldTemplateLoading}
                onClick={handleSaveTemplate}
              >
                Save
              </FHButton>
            </Box>
          </Box>
        </FBoxContainer>
      </Box>
    </PageContainer>
  );
};

export default FieldTemplatesPage;
